import { type ImageQuality } from 'astro';
import { Fragment, type HTMLAttributes, type ImgHTMLAttributes, type SourceHTMLAttributes } from 'react';
import { getURL } from "@module/image-endpoint/client";

type srcSetAttr = string | Array<{
  width: number;
  height?: number;
  quality?: ImageQuality;
  fit?: "cover" | "contain" | "fill" | "inside" | "outside";
  position?: "top" | "right top" | "right" | "right bottom" | "bottom" | "left bottom" | "left" | "left top";
  size?: string;
}>;

interface SourceAttrs extends Omit<SourceHTMLAttributes<HTMLSourceElement>, "srcSet"> {
  srcSet?: srcSetAttr;
  quality?: ImageQuality;
  fit?: "cover" | "contain" | "fill" | "inside" | "outside";
  position?: "top" | "right top" | "right" | "right bottom" | "bottom" | "left bottom" | "left" | "left top";
};

interface Props extends Omit<Omit<ImgHTMLAttributes<HTMLImageElement>, "src">, "srcSet"> {// HTMLAttributes<HTMLElement> {
  classNames?: {
    picture?: string;
    source?: string;
  };
  pictureAttrs?: HTMLAttributes<HTMLElement>;
  src?: string | ImageMetadata | Image;
  width?: string | number;
  height?: string | number;
  srcSet?: string | Array<{
    width: number;
    height?: number;
    quality?: ImageQuality;
    fit?: "cover" | "contain" | "fill" | "inside" | "outside";
    position?: "top" | "right top" | "right" | "right bottom" | "bottom" | "left bottom" | "left" | "left top";
    size?: string;
  }>;
  quality?: ImageQuality;
  fit?: "cover" | "contain" | "fill" | "inside" | "outside";
  position?: "top" | "right top" | "right" | "right bottom" | "bottom" | "left bottom" | "left" | "left top";
  formats?: Array<"avif" | "png" | "webp" | "jpeg" | "jpg" | "svg">;
  sources?: Array<SourceAttrs>;
  fetchpriority?: "high" | "low" | "auto";
};

function getExt(src: string){
  const arr = src.split(".");
  return arr[arr.length - 1];
};
const key = Math.floor(Math.random() * 100000);
export default function Picture({
  className,
  classNames = {},
  pictureAttrs = {},
  src: img,
  width: w,
  height: h,
  alt,
  title,
  srcSet,
  sizes,
  quality,
  fit,
  position,
  formats = ["avif", "webp"],
  loading = "lazy",
  decoding ="async",
  sources,
  ...props
}:Props) {
  if(!img) return <Fragment/>;
  const _w = (typeof w === "string") ? parseInt(w) : w;
  const _h = (typeof h === "string") ? parseInt(h) : h;
  const hasWH = !!_w || !!_h;
  const width = hasWH ? _w : (typeof img === "object" && "width" in img) ? img.width : undefined;
  const height = hasWH ? _h : (typeof img == "object" && "height" in img) ? img.height : undefined;
  const _alt = alt || title || ((typeof img !== "string") ? ("alt" in img ? img.alt : ("title" in img ? img.title : undefined)) : undefined);
  const src = (typeof img === "string") ? img : img.src;
  const format = getExt(src);
  const isSVG = format === "svg";
  // img src
  const srcAttr = !isSVG ? getURL({
    src: src,
    width: width,
    height: height,
    quality: quality,
    format: "webp",
    fit: fit,
    position: position,
  }): src;

  // img sizes
  let sizesAttrArr:Array<string> = [];
  const srcSetAttr = !isSVG ? ((srcSet instanceof Array) ? srcSet.sort((a, b) => b.width - a.width).map((_src, i) => {
    sizesAttrArr.push(_src.size ?? `(max-width: ${_src.width}px) ${_src.width}w`);
    let url = getURL({
      src: src,
      width: _src.width,
      height: _src.height,
      quality: _src.quality,
      format: "webp",
      fit: _src.fit,
      position: _src.position,
    });
    if(srcSet.length === i+1)
      sizesAttrArr.push(`${_src.width}w`);
    return `${url} ${_src.width}w`;
  }).join(", ") : srcSet) : undefined;
  const sizesAttr = !!sizesAttrArr.length ? sizesAttrArr.join(", ") : "auto";

  // sources
  var sourcesAttrs:Array<SourceHTMLAttributes<HTMLSourceElement>> = [];

  !isSVG && formats.forEach(_type => {
    var _sources:Array<SourceAttrs> = (!sources || sources.length <= 0) ? [
      {
        srcSet: srcSet,
        width: width,
        height: height,
        quality: quality,
        fit: fit,
        position: position,
      }
    ] : sources;

    _sources.forEach(({srcSet, width, height, quality, fit, position, ..._sourceOther}) => {
      let srcSetAttr = (srcSet instanceof Array) ? srcSet.map(_srcSet => {
        let url = getURL({
          src: src,
          width: _srcSet.width,
          height: _srcSet.height,
          quality: _srcSet.quality,
          format: _type,
          fit: _srcSet.fit,
          position: _srcSet.position,
        });
        return `${url} ${_srcSet.width}w`;
      }).join(", ") : srcSet;
      sourcesAttrs.push({
        type: `image/${(_type==="svg")?"svg+xml":_type}`,
        srcSet: srcSetAttr ?? getURL({
          src: src,
          width: width,
          height: height,
          quality: quality,
          format: _type,
          fit: fit,
          position: position,
        }),
        width: width,
        height: height,
        ..._sourceOther
      });
    });
  });

  return (
    <picture className={classNames.picture} {...pictureAttrs}>
      { sourcesAttrs.map(({className, ..._attrs}, i) => <source key={`${key}-${i}`} className={classNames.source} {..._attrs}/>) }
      <img className={className} src={srcAttr} srcSet={srcSetAttr} width={width} height={height} sizes={ sizes ?? sizesAttr } alt={_alt || undefined} loading={loading} decoding={decoding} {...props}/>
    </picture>
  );
}